import { FilterTh, describeColumnFilters } from '../common/column-filter-indicator'
/**
 * Clients list — the operator's tenant surface.
 *
 * The entry point for onboarding a customer, which before this page
 * meant an SSM session and a Python REPL. Search, sorting and paging
 * ALL run server-side: the page requests exactly the 25 rows it shows,
 * with `sortField`/`sortDirection` applied in SQL before the slice.
 * English-only internal tooling.
 *
 * It used to fetch the newest 200 and sort/slice them in the browser,
 * which put every client past row 200 out of reach unless a search
 * happened to narrow onto them — invisible from the UI, because the
 * table looked complete. Local databases run to five figures of
 * clients, so "the newest 200" was not a window on the data, it was a
 * different dataset.
 */
import { useEffect, useState } from 'react'
import { buttonVariants, Icon } from '@speaking-coach/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../api/query-keys'
import { InternalBadge } from '../cohorts/cohort-form-fields'
import {
  ROW_LINK,
  ROW_LINK_ABOVE,
  TABLE,
  TABLE_WRAP,
  TABLE_HEAD_ROW,
  TABLE_ROW,
} from '../common/table'
import {
  ClassificationFilter,
  ClearFiltersButton,
  FilteredEmptyState,
  ListPage,
  ListPagination,
  SearchField,
  classificationParam,
} from '../common/list-page'
import { Link } from 'react-router-dom'
import { SortableTh } from '../common/sortable-th'
import type { SortDir } from '../common/sortable-th'
import { adminClientsApi, messageForClientError } from './api'
import type { ClientRow, ClientSortField, ListClientsParams } from './types'

type LoadStatus = 'loading' | 'loaded' | 'error'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 250

type SortKey = ClientSortField

/** ISO timestamp → YYYY-MM-DD. Locale-independent on purpose. */
function isoDate(value: string | null): string {
  return value ? value.slice(0, 10) : '—'
}

export function ClientsListPage(): JSX.Element {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  // '' = both, 'true' = internal only, 'false' = customers only. Kept as the
  // raw string so "not set" and "set to false" stay distinguishable — the same
  // three-state the API takes, and a boolean here could not express it.
  //
  // Local state rather than a URL param, matching this page's own search. The
  // student roster keeps its filters in the URL because the client page
  // deep-links into a pre-filtered roster; nothing deep-links into this one.
  const [isInternalFilter, setIsInternalFilter] = useState('')
  const [page, setPage] = useState(0)
  // Newest first — matches the order the API returns, so the first
  // paint does not reshuffle.
  const [sortKey, setSortKey] = useState<SortKey>('createdAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  function toggleSort(key: SortKey): void {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
    setPage(0)
  }

  // The page reset rides the COMMITTED term, not the keystroke. Resetting
  // per keystroke raced this debounce: `page` went to 0 immediately while
  // `search` landed 250 ms later, so in between Next was still clickable
  // against the previous term's total — and a request could go out pairing
  // a new search with an offset that belonged to the old one. Committing
  // both in the same tick also stops the old term's page 0 being refetched
  // on the way past.
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = query.trim()
      if (next === search) return
      setSearch(next)
      setPage(0)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query, search])

  // Every parameter is part of the key, so each distinct query is its own
  // cache entry: going back to a previous term or page is instant, and a
  // slow reply for an EARLIER query resolves into the entry it belongs to
  // rather than painting over a newer one. That is the whole
  // stale-response guard — the cancel-on-unmount bookkeeping it replaces
  // is the library's job now.
  //
  // `offset` belongs in the key for the same reason: paging is server-side,
  // so page 2 is a different result rather than a different slice of one.
  const params: ListClientsParams = {
    search: search || undefined,
    isInternal: classificationParam(isInternalFilter),
    sortField: sortKey,
    sortDirection: sortDir,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }
  const clientsQuery = useQuery({
    queryKey: queryKeys.clientList(params),
    queryFn: () => adminClientsApi.list(params),
    // Typing in the search mints a new key per keystroke; without this the
    // table unmounts into "Loading…" and FLASHES on every character. The
    // previous rows stay (dimmed below) until the next page arrives.
    placeholderData: keepPreviousData,
  })

  const clients: ClientRow[] = clientsQuery.data?.clients ?? []
  const total = clientsQuery.data?.total ?? 0

  // An offset past the end is a DEAD END, not just an empty page: the table
  // and the pager both live inside the has-rows branch below, so there is
  // no control left to page back with — and with no filters set the empty
  // state reads "No clients yet" beside a header badge showing the real
  // count. Client-side slicing could never produce this; server paging can,
  // whenever the result set shrinks under an operator parked on a later
  // page. So snap back to the last page that exists and let it refetch.
  //
  // Gated on settled data: mid-flight `keepPreviousData` rows belong to the
  // previous key and would make this fire against the wrong total.
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)
  const strandedPastTheEnd =
    clientsQuery.isSuccess &&
    !clientsQuery.isPlaceholderData &&
    total > 0 &&
    clients.length === 0 &&
    page > 0
  useEffect(() => {
    if (strandedPastTheEnd) setPage(lastPage)
  }, [strandedPastTheEnd, lastPage])
  const status: LoadStatus = clientsQuery.isPending
    ? 'loading'
    : clientsQuery.isError
      ? 'error'
      : 'loaded'
  const loadError =
    clientsQuery.error !== null
      ? messageForClientError(clientsQuery.error)
      : null

  // The search term counts: a "Clear filters" that leaves a search in place is
  // the button not doing what it says.
  const hasFilters = search !== '' || isInternalFilter !== ''

  function clearFilters(): void {
    setQuery('')
    setIsInternalFilter('')
    setPage(0)
  }

  // From `total`, not from the rows in hand — the rows in hand ARE one page.
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pageRows = clients

  // The offset the rows ON SCREEN were actually fetched with. During a
  // `keepPreviousData` window `page` has already moved while the rows and
  // `total` still belong to the previous key, so a span derived from `page`
  // claimed "Showing 26–30 of 30" above rows 1–25. Adjusting state during
  // render (rather than in an effect) keeps the number in step with the
  // rows it labels, with no frame showing the mismatch.
  const currentOffset = page * PAGE_SIZE
  const [renderedOffset, setRenderedOffset] = useState(0)
  if (
    clientsQuery.isSuccess &&
    !clientsQuery.isPlaceholderData &&
    renderedOffset !== currentOffset
  ) {
    setRenderedOffset(currentOffset)
  }
  const start = clientsQuery.isPlaceholderData ? renderedOffset : currentOffset

  return (
    <ListPage
      title="Clients"
      count={status === 'loaded' ? total : undefined}
      action={
        <Link
          to="/admin/clients/new"
          className={buttonVariants({ variant: 'primary' }) + ' whitespace-nowrap'}
        >
          <Icon name="add" />
          New client
        </Link>
      }
      filterAction={
        <ClearFiltersButton hasFilters={hasFilters} onClear={clearFilters} />
      }
      filters={
        <>
          <ClassificationFilter
            id="client-internal-filter"
            value={isInternalFilter}
            help="Internal clients are excluded from customer analytics — and every cohort and student inside one is internal too, so this narrows a whole tree at once."
            onChange={(next) => {
              setIsInternalFilter(next)
              setPage(0)
            }}
          />
        </>
      }
      search={
        <SearchField
          id="client-search"
          label="Search clients"
          placeholder="Search name or slug..."
          value={query}
          // No `setPage(0)` here — the debounce effect commits the term and
          // the page reset together, so the two cannot disagree mid-flight.
          onChange={(e) => setQuery(e.target.value)}
        />
      }
      // The rail's one filter; the search stays visible when collapsed.
      activeFilterCount={isInternalFilter !== '' ? 1 : 0}
      pagination={
        status === 'loaded' && pageRows.length > 0 ? (
          <ListPagination
            // No "newest N loaded" caveat any more: the window IS the
            // page, so all `total` rows are reachable by paging.
            summary={
              <>
                Showing {start + 1}–
                {Math.min(start + pageRows.length, total)} of {total}
              </>
            }
            page={safePage}
            pageCount={pageCount}
            previousDisabled={safePage === 0}
            nextDisabled={safePage >= pageCount - 1}
            onPrevious={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          />
        ) : undefined
      }
    >
      <>
        {status === 'loading' && (
          <p className="text-text-secondary">Loading clients…</p>
        )}
        {status === 'error' && (
          <div
            role="alert"
            className="bg-surface-card border border-danger/40 p-4 rounded-token-card"
          >
            <p className="font-medium">Could not load clients</p>
            <p className="text-text-secondary text-body mt-1">{loadError}</p>
          </div>
        )}

        {status === 'loaded' && pageRows.length === 0 && (
          <FilteredEmptyState
            hasFilters={hasFilters}
            noun="clients"
            onClear={clearFilters}
            emptyHint="No clients yet. Use New client to create one."
          />
        )}

        {/* Stale rows stay visible while the next result is in flight
            (keepPreviousData) — dimmed, so the refresh is honest without
            the table unmounting into a flash of "Loading…". */}
        {status === 'loaded' && pageRows.length > 0 && (
          <div
            className={
              clientsQuery.isPlaceholderData
                ? 'opacity-60 transition-opacity'
                : 'transition-opacity'
            }
          >
          <>
            <div className={TABLE_WRAP}>
              <table className={TABLE}>
              <thead>
                <tr className={TABLE_HEAD_ROW}>
                  <SortableTh
                    label="Name"
                    filterDescription={describeColumnFilters(search ? `Search: “${search}”` : undefined, isInternalFilter === 'true' ? 'Internal only' : isInternalFilter === 'false' ? 'Customers only' : undefined)}
                    columnKey="name"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <FilterTh className="p-3 font-medium" label="Slug" filterDescription={search ? `Search: “${search}”` : undefined} />
                  <th className="p-3 font-medium">Students</th>
                  <th className="p-3 font-medium">HR users</th>
                  <th className="p-3 font-medium">Trophy target</th>
                  <SortableTh
                    label="Created"
                    columnKey="createdAt"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((a) => (
                  <tr
                    key={a.clientId}
                    className={TABLE_ROW}
                  >
                    <td className="p-3">
                      {/* The row's primary link: clicking anywhere in the
                          row opens this client. Still a real anchor, so
                          cmd-click and keyboard both work. */}
                      <Link to={`/admin/clients/${a.clientId}`} className={ROW_LINK}>
                        {a.name}
                      </Link>
                      {a.isInternal && (
                        <span className="ml-2" title="Internal: excluded from customer analytics. Not an access flag.">
                          <InternalBadge />
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-table-secondary">
                      {/* Same destination as the row link, so lifting it
                          above the overlay changes nothing functionally —
                          it just keeps the hover affordance honest. */}
                      <Link
                        to={`/admin/clients/${a.clientId}`}
                        aria-label={`${a.name} (${a.slug})`}
                        className={`text-text-secondary hover:text-text-primary underline ${ROW_LINK_ABOVE}`}
                      >
                        {a.slug}
                      </Link>
                    </td>
                    <td className="p-3">{a.studentCount}</td>
                    <td className="p-3">{a.hrUserCount}</td>
                    <td className="p-3 text-text-secondary">
                      {a.contractTrophyTarget}
                    </td>
                    <td className="p-3 text-text-secondary">
                      {isoDate(a.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

          </>
          </div>
        )}
      </>
    </ListPage>
  )
}
